import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import * as Icons from 'react-feather';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { getStorageValues } from '../../../shared/CommonFunctions';
import SKAlerts, { CreateAlert } from '../../../shared/common-alerts/SKAlerts';
import { ReactComponent as SortArrow } from '../../../shared/img/chevron-up-down.svg';
import RichTextEditor from '../../../shared/rich-text/RichTextEditor';
import { isEmptyHtml, stripHtml, toEditorHtml } from '../../../shared/rich-text/richTextUtils';

const sortSpecRows = (rows = []) =>
  [...rows].sort(
    (a, b) =>
      Number(a.DisplayOrder || 0) - Number(b.DisplayOrder || 0) ||
      Number(a.SpecId || 0) - Number(b.SpecId || 0),
  );

const getNextDisplayOrder = (rows = []) => {
  if (!rows.length) return 1;
  return Math.max(...rows.map((row) => Number(row.DisplayOrder) || 0)) + 1;
};

const toNullableHtml = (html) => (isEmptyHtml(html) ? null : html);

function ProductSpecificationsTab({
  productId,
  specifications = [],
  readOnly = false,
  onRefresh,
  onOrderChange,
  specOrderSaveToken = 0,
}) {
  const [alert, setAlert] = useState(null);
  const [specRows, setSpecRows] = useState(() => sortSpecRows(specifications));
  const [orderDirty, setOrderDirty] = useState(false);
  const [selectedSpecId, setSelectedSpecId] = useState(null);
  const [editorSessionKey, setEditorSessionKey] = useState(0);
  const [draft, setDraft] = useState({
    SpecId: null,
    SpecGroup: '',
    SpecLabel: '',
    SpecValue: '',
  });

  useEffect(() => {
    if (specOrderSaveToken > 0) {
      setOrderDirty(false);
    }
  }, [specOrderSaveToken]);

  useEffect(() => {
    if (orderDirty) return;
    const sorted = sortSpecRows(specifications);
    setSpecRows(sorted);
    if (selectedSpecId && !sorted.some((r) => r.SpecId === selectedSpecId)) {
      setSelectedSpecId(null);
    }
  }, [specifications, orderDirty, selectedSpecId]);

  useEffect(() => {
    if (!onOrderChange || !productId) return;
    onOrderChange({
      productId,
      orderList: specRows.map((row, index) => ({
        SpecId: row.SpecId,
        DisplayOrder: index + 1,
      })),
      dirty: orderDirty,
    });
  }, [specRows, orderDirty, productId, onOrderChange]);

  const resetDraft = () => {
    setDraft({ SpecId: null, SpecGroup: '', SpecLabel: '', SpecValue: '' });
    setSelectedSpecId(null);
    setEditorSessionKey((key) => key + 1);
  };

  const selectedIndex = useMemo(
    () => specRows.findIndex((row) => row.SpecId === selectedSpecId),
    [specRows, selectedSpecId],
  );

  const canMoveUp = selectedIndex > 0;
  const canMoveDown = selectedIndex >= 0 && selectedIndex < specRows.length - 1;

  const moveSelected = useCallback((direction) => {
    if (selectedIndex < 0) return;
    const target = direction === 'up' ? selectedIndex - 1 : selectedIndex + 1;
    if (target < 0 || target >= specRows.length) return;

    setSpecRows((prev) => {
      const updated = [...prev];
      [updated[selectedIndex], updated[target]] = [updated[target], updated[selectedIndex]];
      return updated.map((row, i) => ({ ...row, DisplayOrder: i + 1 }));
    });
    setOrderDirty(true);
  }, [selectedIndex, specRows.length]);

  const saveSpec = async () => {
    if (!productId) {
      CreateAlert('Save product details first', 'Warning', setAlert);
      return;
    }
    if (isEmptyHtml(draft.SpecLabel)) {
      CreateAlert('Specification label is required', 'Warning', setAlert);
      return;
    }

    let displayOrder = getNextDisplayOrder(specRows);
    if (draft.SpecId) {
      const existing = specRows.find((row) => row.SpecId === draft.SpecId);
      displayOrder = existing?.DisplayOrder ?? displayOrder;
    }

    try {
      const response = await axios.post(`${getApiBaseUrl()}/ec-product/specification/save`, {
        SpecId: draft.SpecId,
        ProductId: productId,
        SpecGroup: stripHtml(draft.SpecGroup) || null,
        SpecLabel: draft.SpecLabel,
        SpecValue: toNullableHtml(draft.SpecValue),
        DisplayOrder: displayOrder,
        UserId: getStorageValues('userId'),
      });
      const result = response.data?.data;
      if (result?.success === 1) {
        CreateAlert(result.message || 'Specification saved', 'Success', setAlert);
        resetDraft();
        if (onRefresh) onRefresh();
      } else {
        CreateAlert(result?.message || 'Failed to save specification', 'Error', setAlert);
      }
    } catch (err) {
      console.error(err);
      CreateAlert('Failed to save specification', 'Error', setAlert);
    }
  };

  const deleteSpec = useCallback(async (specId) => {
    try {
      const response = await axios.post(`${getApiBaseUrl()}/ec-product/specification/delete`, {
        SpecId: specId,
        UserId: getStorageValues('userId'),
      });
      if (response.data?.data?.success === 1) {
        if (selectedSpecId === specId) {
          setSelectedSpecId(null);
        }
        CreateAlert('Specification deleted', 'Success', setAlert);
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      console.error(err);
      CreateAlert('Failed to delete specification', 'Error', setAlert);
    }
  }, [onRefresh, selectedSpecId]);

  const editRow = useCallback((row) => {
    setSelectedSpecId(row.SpecId);
    setDraft({
      SpecId: row.SpecId,
      SpecGroup: stripHtml(row.SpecGroup) || '',
      SpecLabel: row.SpecLabel || '',
      SpecValue: row.SpecValue || '',
    });
    setEditorSessionKey((key) => key + 1);
    document.querySelector('.sk-product-spec-form')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  const conditionalRowStyles = useMemo(
    () => [
      {
        when: (row) => row.SpecId === selectedSpecId,
        style: {
          backgroundColor: 'rgba(18, 97, 160, 0.12)',
          borderLeft: '3px solid #1261A0',
        },
      },
    ],
    [selectedSpecId],
  );

  const columns = useMemo(() => {
    const cols = [
      {
        name: (
          <div className="titlename">
            <span>Group</span>
          </div>
        ),
        cell: (row) => {
          const preview = stripHtml(row.SpecGroup);
          return <span title={preview}>{preview || '-'}</span>;
        },
        width: '120px',
        sortable: false,
        sortIcon: <SortArrow />,
      },
      {
        name: (
          <div className="titlename">
            <span>Label</span>
          </div>
        ),
        cell: (row) => {
          const preview = stripHtml(row.SpecLabel);
          return <span title={preview}>{preview || '-'}</span>;
        },
        width: '160px',
        sortable: false,
        sortIcon: <SortArrow />,
      },
      {
        name: (
          <div className="titlename">
            <span>Value</span>
          </div>
        ),
        cell: (row) => {
          const preview = stripHtml(row.SpecValue);
          return (
            <span title={preview} className="sk-spec-value-cell">
              {preview || '-'}
            </span>
          );
        },
        sortable: false,
        sortIcon: <SortArrow />,
      },
    ];

    if (!readOnly) {
      cols.push({
        name: <div className="titlename">Actions</div>,
        cell: (row) => (
          <div className="edit-delete-action d-flex align-items-center justify-content-end">
            <Link
              className="me-2 p-2"
              to=""
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                editRow(row);
              }}
            >
              <Icons.Edit className="feather-edit" />
            </Link>
            <Link
              className="confirm-text p-2"
              to=""
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                deleteSpec(row.SpecId);
              }}
            >
              <Icons.Trash2 className="feather-trash-2" />
            </Link>
          </div>
        ),
        right: true,
        width: '120px',
        sortable: false,
      });
    }

    return cols;
  }, [readOnly, editRow, deleteSpec]);

  if (!productId) {
    return <div className="alert alert-info mb-0">Save the product details first to add specifications.</div>;
  }

  return (
    <>
      <SKAlerts alert={alert} />
      {!readOnly && (
        <div className="card mb-3 sk-product-spec-form">
          <div className="card-body">
            <h6 className="mb-2">{draft.SpecId ? 'Edit Specification' : 'Add Specification'}</h6>
            <div className="row g-2">
              <div className="col-lg-6">
                <input
                  type="text"
                  className="form-control"
                  value={draft.SpecGroup}
                  onChange={(e) => setDraft((p) => ({ ...p, SpecGroup: e.target.value }))}
                  placeholder="Group (e.g. Camera, Description)"
                  readOnly={readOnly}
                />
              </div>
              <div className="col-lg-6">
                <RichTextEditor
                  loadKey={`${editorSessionKey}-label`}
                  value={toEditorHtml(draft.SpecLabel)}
                  onChange={(html) => setDraft((p) => ({ ...p, SpecLabel: html }))}
                  placeholder="Label"
                  readOnly={readOnly}
                />
              </div>
            </div>
            <div className="row g-2 mt-1">
              <div className="col-10">
                <RichTextEditor
                  loadKey={`${editorSessionKey}-value`}
                  value={toEditorHtml(draft.SpecValue)}
                  onChange={(html) => setDraft((p) => ({ ...p, SpecValue: html }))}
                  placeholder="Value"
                  readOnly={readOnly}
                />
              </div>
              <div className="col-lg-2 d-flex align-items-end gap-2">
                <button type="button" className="btn btn-submit" onClick={saveSpec}>
                  {draft.SpecId ? 'Save' : 'Add'}
                </button>
                {draft.SpecId ? (
                  <button type="button" className="btn btn-cancel" onClick={resetDraft}>Cancel</button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="d-flex align-items-stretch gap-2 sk-product-specs-grid-row">
        <div className="flex-grow-1 min-width-0 card table-list-card sk-product-specs-table">
          <div className="card-body">
            <DataTable
              className="table-responsive product-list"
              columns={columns}
              data={specRows}
              keyField="SpecId"
              pagination={false}
              fixedHeader
              fixedHeaderScrollHeight="320px"
              sortIcon={<SortArrow />}
              persistTableHead
              conditionalRowStyles={conditionalRowStyles}
              onRowClicked={(row) => setSelectedSpecId(row.SpecId)}
              pointerOnHover
              highlightOnHover
              noDataComponent={<span className="text-muted">No specifications added.</span>}
            />
          </div>
        </div>
        {!readOnly ? (
          <div className="sk-spec-order-controls d-flex flex-column justify-content-center gap-2">
            <button
              type="button"
              className="sk-order-btn btn btn-outline-primary"
              disabled={!canMoveUp}
              title="Move selected up"
              aria-label="Move selected up"
              onClick={() => moveSelected('up')}
            >
              <Icons.ChevronUp size={20} />
            </button>
            <button
              type="button"
              className="sk-order-btn btn btn-outline-primary"
              disabled={!canMoveDown}
              title="Move selected down"
              aria-label="Move selected down"
              onClick={() => moveSelected('down')}
            >
              <Icons.ChevronDown size={20} />
            </button>
          </div>
        ) : null}
      </div>
    </>
  );
}

export default ProductSpecificationsTab;
